import React, { useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Input,
  Modal,
  Segmented,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import {
  ArrowDownOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import dayjs from "dayjs";
import ExcelExport from "components/shared-components/ExcelExport";
import { formatPrice } from "functions/formatters";
import { getPriceImportOverviewExcelData } from "functions/excel";
import { PRICE_IMPORT_STATUS } from "functions/priceImport";
import { COMPANY_CONSTANTS } from "constants/CompanyConstants";
import { DATE_FORMAT_DD_MM_YYYY } from "constants/DateConstant";
import { getErrorLabel, getNotesText, getStatusLabel, getWarningTags } from "./labels";

const { Text, Title } = Typography;

const FILTERS = {
  ALL: "all",
  CHANGES: "changes",
  ATTENTION: "attention",
  UNCHANGED: "unchanged",
  ERRORS: "errors",
};

const ReviewStep = ({ lines, summary, fileName, sheetName, selectedKeys, onSelectionChange, onApply, onRestart }) => {
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();
  const [filter, setFilter] = useState(summary.changeCount > 0 ? FILTERS.CHANGES : FILTERS.ALL);
  const [searchText, setSearchText] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmChecked, setConfirmChecked] = useState(false);

  const changeLines = useMemo(
    () => lines.filter((line) => line.status === PRICE_IMPORT_STATUS.CHANGE),
    [lines]
  );

  const visibleLines = useMemo(() => {
    let result = lines;

    if (filter === FILTERS.CHANGES) result = result.filter((line) => line.status === PRICE_IMPORT_STATUS.CHANGE);
    if (filter === FILTERS.UNCHANGED) result = result.filter((line) => line.status === PRICE_IMPORT_STATUS.UNCHANGED);
    if (filter === FILTERS.ERRORS) result = result.filter((line) => line.status === PRICE_IMPORT_STATUS.ERROR);
    if (filter === FILTERS.ATTENTION) {
      result = result.filter((line) => line.status !== PRICE_IMPORT_STATUS.ERROR && line.warnings.length > 0);
    }

    const query = searchText.trim().toLowerCase();
    if (query) {
      result = result.filter((line) =>
        [line.productName, line.productCode, line.fileName, line.fileCode]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(query))
      );
    }

    return result;
  }, [lines, filter, searchText]);

  const selectedLines = useMemo(
    () => changeLines.filter((line) => selectedKeys.includes(line.key)),
    [changeLines, selectedKeys]
  );

  const selectedWarningCount = selectedLines.filter((line) => line.warnings.length > 0).length;

  const columns = [
    {
      title: t("price_import.review.column_row"),
      dataIndex: "rowNumber",
      width: 80,
      sorter: (a, b) => a.rowNumber - b.rowNumber,
      render: (rowNumber) => <Text type="secondary">{rowNumber}</Text>,
    },
    {
      title: t("price_import.review.column_product"),
      dataIndex: "productName",
      sorter: (a, b) =>
        String(a.productName || a.fileName || "").localeCompare(String(b.productName || b.fileName || "")),
      render: (_, line) => (
        <div>
          <div style={{ fontWeight: 500 }}>{line.productName || line.fileName || "-"}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {line.productCode || line.fileCode || "-"}
          </Text>
          {!!line.fileName && line.productName && line.fileName !== line.productName && (
            <div style={{ fontSize: 12, color: "#d48806" }}>
              {t("price_import.review.file_name_label", { name: line.fileName })}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t("price_import.review.column_current_price"),
      dataIndex: "oldPrice",
      width: 140,
      align: "right",
      sorter: (a, b) => (a.oldPrice || 0) - (b.oldPrice || 0),
      render: (oldPrice, line) => {
        if (oldPrice === null || oldPrice === undefined) return <Text type="secondary">-</Text>;
        return (
          <div style={{ whiteSpace: "nowrap" }}>
            <div>€ {formatPrice(oldPrice)}</div>
            {line.discount > 0 && (
              <Tooltip
                title={t("price_import.review.shop_price_with_discount", {
                  discount: line.discount,
                  price: formatPrice(Math.round(oldPrice * (1 - line.discount / 100) * 100) / 100),
                })}
              >
                <Text type="secondary" style={{ fontSize: 12 }}>
                  -{line.discount}%
                </Text>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: "",
      dataIndex: "arrow",
      width: 40,
      align: "center",
      render: (_, line) =>
        line.status === PRICE_IMPORT_STATUS.CHANGE ? <ArrowRightOutlined style={{ color: "#8c8c8c" }} /> : null,
    },
    {
      title: t("price_import.review.column_new_price"),
      dataIndex: "newPrice",
      width: 150,
      align: "right",
      sorter: (a, b) => (a.newPrice || 0) - (b.newPrice || 0),
      render: (newPrice, line) => {
        if (newPrice === null || newPrice === undefined) {
          return <Text type="secondary">{String(line.rawPrice ?? "-")}</Text>;
        }
        if (line.status === PRICE_IMPORT_STATUS.UNCHANGED) {
          return (
            <Text type="secondary" style={{ whiteSpace: "nowrap" }}>
              € {formatPrice(newPrice)}
            </Text>
          );
        }
        return (
          <Text strong style={{ whiteSpace: "nowrap", fontSize: 15 }}>
            € {formatPrice(newPrice)}
          </Text>
        );
      },
    },
    {
      title: t("price_import.review.column_difference"),
      dataIndex: "delta",
      width: 150,
      align: "right",
      sorter: (a, b) => (a.deltaPercentage ?? 0) - (b.deltaPercentage ?? 0),
      render: (delta, line) => {
        if (line.status !== PRICE_IMPORT_STATUS.CHANGE || delta === null) return <Text type="secondary">-</Text>;
        const isUp = delta > 0;
        const color = isUp ? "#cf1322" : "#389e0d";
        return (
          <div style={{ color, whiteSpace: "nowrap" }}>
            {isUp ? <ArrowUpOutlined /> : <ArrowDownOutlined />} € {formatPrice(Math.abs(delta))}
            {line.deltaPercentage !== null && (
              <div style={{ fontSize: 12 }}>
                {line.deltaPercentage > 0 ? "+" : ""}
                {formatPrice(line.deltaPercentage)}%
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t("price_import.review.column_notes"),
      dataIndex: "warnings",
      width: 300,
      render: (_, line) => (
        <Space size={[4, 4]} wrap>
          {line.status === PRICE_IMPORT_STATUS.ERROR && (
            <Tag color="red" icon={<ExclamationCircleOutlined />}>
              {getErrorLabel(t, line)}
            </Tag>
          )}
          {getWarningTags(t, line).map((tag) =>
            tag.tooltip ? (
              <Tooltip key={tag.key} title={tag.tooltip}>
                <Tag color={tag.color} style={{ cursor: "help" }}>
                  {tag.label} <InfoCircleOutlined />
                </Tag>
              </Tooltip>
            ) : (
              <Tag key={tag.key} color={tag.color}>
                {tag.label}
              </Tag>
            )
          )}
          {!!line.matches?.length && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {line.matches.map((match) => match.name).join(", ")}
            </Text>
          )}
        </Space>
      ),
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys) => onSelectionChange(keys),
    getCheckboxProps: (line) => ({ disabled: line.status !== PRICE_IMPORT_STATUS.CHANGE }),
    columnWidth: 48,
  };

  const rowClassName = (line) => {
    if (line.status === PRICE_IMPORT_STATUS.ERROR) return "price-import-row--error";
    if (line.warnings.length > 0) return "price-import-row--warning";
    if (line.status === PRICE_IMPORT_STATUS.UNCHANGED) return "price-import-row--unchanged";
    return "";
  };

  const openConfirm = () => {
    if (selectedLines.length === 0) {
      messageApi.warning(t("price_import.review.nothing_selected"));
      return;
    }
    setConfirmChecked(false);
    setConfirmOpen(true);
  };

  const excelData = useMemo(
    () =>
      getPriceImportOverviewExcelData(visibleLines, {
        statusLabel: (line) => getStatusLabel(t, line),
        notesLabel: (line) => getNotesText(t, line),
      }),
    [visibleLines, t]
  );

  // Eén melding in plaats van een blok per soort; de aantallen per soort staan al op de filters.
  const notices = [];
  if (summary.errorCount > 0) notices.push(t("price_import.review.notice_errors", { count: summary.errorCount }));
  if (summary.warningCount > 0) notices.push(t("price_import.review.notice_warnings", { count: summary.warningCount }));

  return (
    <>
      {contextHolder}
      <Card>
        <Title level={5} style={{ marginBottom: 2 }}>
          {summary.changeCount > 0
            ? t("price_import.review.heading", { count: summary.changeCount, total: summary.totalRows })
            : t("price_import.review.heading_none", { total: summary.totalRows })}
        </Title>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {t("price_import.review.file_info", { fileName, sheetName })}
          {summary.notInFileCount > 0 && ` · ${t("price_import.review.not_in_file_note", { count: summary.notInFileCount })}`}
        </Text>

        {notices.length > 0 && (
          <Alert
            style={{ marginTop: 16 }}
            type={summary.errorCount > 0 ? "error" : "warning"}
            showIcon
            message={`${notices.join(" ")} ${t("price_import.review.notice_hint")}`}
          />
        )}

        <Toolbar>
          <Segmented
            value={filter}
            onChange={setFilter}
            options={[
              { label: `${t("price_import.review.filter_all")} (${summary.totalRows})`, value: FILTERS.ALL },
              { label: `${t("price_import.review.filter_changes")} (${summary.changeCount})`, value: FILTERS.CHANGES },
              {
                label: `${t("price_import.review.filter_attention")} (${summary.warningCount})`,
                value: FILTERS.ATTENTION,
              },
              {
                label: `${t("price_import.review.filter_unchanged")} (${summary.unchangedCount})`,
                value: FILTERS.UNCHANGED,
              },
              { label: `${t("price_import.review.filter_errors")} (${summary.errorCount})`, value: FILTERS.ERRORS },
            ]}
          />
          <Space wrap>
            <Input.Search
              allowClear
              placeholder={t("price_import.review.search_placeholder")}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              style={{ width: 220 }}
            />
            <ExcelExport
              data={excelData}
              fileName={`${COMPANY_CONSTANTS.WEBSITE_NAME} prijswijzigingen ${dayjs().format(DATE_FORMAT_DD_MM_YYYY)}`}
            >
              <Button type="text" icon={<FileExcelOutlined />}>
                {t("price_import.review.export_overview")}
              </Button>
            </ExcelExport>
          </Space>
        </Toolbar>

        <StyledTableWrapper className="table-responsive">
          <Table
            rowKey="key"
            size="middle"
            columns={columns}
            dataSource={visibleLines}
            rowSelection={rowSelection}
            rowClassName={rowClassName}
            scroll={{ y: 520, x: 1100 }}
            pagination={{
              defaultPageSize: 100,
              pageSizeOptions: ["50", "100", "200", "500"],
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
            }}
            locale={{
              emptyText: (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={t("price_import.review.no_rows_in_filter")}
                />
              ),
            }}
          />
        </StyledTableWrapper>

        {/* De tabel scrollt intern, dus deze balk blijft in beeld zonder hem te laten zweven. */}
        <Footer>
          <Space size={4}>
            <Text type="secondary">
              {t("price_import.review.selected_count", {
                selected: selectedLines.length,
                total: summary.changeCount,
              })}
            </Text>
            {selectedLines.length < summary.changeCount && (
              <Button type="link" size="small" onClick={() => onSelectionChange(changeLines.map((line) => line.key))}>
                {t("price_import.review.select_all")}
              </Button>
            )}
          </Space>
          <Space>
            <Button onClick={onRestart}>{t("price_import.review.back")}</Button>
            <Button type="primary" onClick={openConfirm} disabled={selectedLines.length === 0}>
              {t("price_import.review.apply_button_count", { count: selectedLines.length })}
            </Button>
          </Space>
        </Footer>
      </Card>

      <Modal
        open={confirmOpen}
        title={t("price_import.confirm.title")}
        okText={t("price_import.confirm.ok")}
        cancelText={t("cancel")}
        okButtonProps={{ danger: true, disabled: !confirmChecked }}
        onCancel={() => setConfirmOpen(false)}
        onOk={() => {
          setConfirmOpen(false);
          onApply(selectedLines);
        }}
      >
        <p style={{ marginBottom: 8 }}>{t("price_import.confirm.body", { count: selectedLines.length })}</p>
        {selectedWarningCount > 0 && (
          <p style={{ marginBottom: 8, color: "#d48806" }}>
            {t("price_import.confirm.attention", { count: selectedWarningCount })}
          </p>
        )}
        <Text type="secondary">{t("price_import.confirm.backup")}</Text>
        <div style={{ marginTop: 16 }}>
          <Checkbox checked={confirmChecked} onChange={(event) => setConfirmChecked(event.target.checked)}>
            {t("price_import.confirm.checkbox")}
          </Checkbox>
        </div>
      </Modal>
    </>
  );
};

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin: 20px 0 12px;
`;

const StyledTableWrapper = styled.div`
  .price-import-row--error > td {
    background-color: #fff1f0;
  }
  .price-import-row--warning > td {
    background-color: #fffbe6;
  }
  .price-import-row--unchanged > td {
    color: rgba(0, 0, 0, 0.45);
  }
  .ant-table-tbody > tr.price-import-row--error:hover > td,
  .ant-table-tbody > tr.price-import-row--warning:hover > td {
    background-color: #fafafa;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #f0f0f0;
`;

export default ReviewStep;
